import React, { useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye } from 'lucide-react';
import AllFieldsPanel from './AllFieldsPanel';
import AttachmentsTab from './AttachmentsTab';
import { useRecordDetail, fmtMoney, fmtDate, yesNo, statusLabel } from './recordUtils';
import '../nativeitems/NativeItems.css';

// Vendor record (Native, read-only) — tasks #62/#60. The first vendor record
// page anywhere in the platform: identity, A/P standing, 1099 fields
// (taxidnum / printoncheckas promotions), recent POs + bills, and the full
// ns_raw capture. Vendors have no packages/related registry entry yet, so the
// Related tab is the recent-PO/bill view itself.

const PO_STATUS = {
  A: 'Pending Approval', B: 'Pending Receipt', C: 'Rejected',
  D: 'Partially Received', E: 'Pending Billing / Partially Received',
  F: 'Pending Bill', G: 'Fully Billed', H: 'Closed',
  pending: 'Pending', received: 'Received', partial: 'Partially Received',
  billed: 'Billed', closed: 'Closed', cancelled: 'Cancelled',
};

export default function VendorRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const { data, loading, error } = useRecordDetail(`/api/native-records/vendors/${encodeURIComponent(id)}`);

  if (loading) return <div className="nit-page"><div className="nit-loading">Loading vendor…</div></div>;
  if (error) {
    return (
      <div className="nit-page">
        <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>
        <div className="nit-error">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { record, recentPurchaseOrders, recentBills } = data;
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'pos', label: 'Purchase orders', count: recentPurchaseOrders.length },
    { key: 'bills', label: 'Bills', count: recentBills.length },
    { key: 'fields', label: 'All fields' },
    { key: 'attachments', label: 'Attachments' },
  ];

  return (
    <div className="nit-page">
      <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>

      <div className="nit-item-head" style={{ marginTop: 8 }}>
        <h1>{record.name || record.alt_name || 'Vendor'}</h1>
        {record.is_active === false && <span className="nit-tag nit-tag-inactive">inactive</span>}
        {record.is_1099_eligible && <span className="nit-tag">1099</span>}
        {record.ns_internal_id && <span className="nit-tag">NS {record.ns_internal_id}</span>}
        <span className="nit-readonly"><Eye size={12} /> read-only</span>
        <Link to={`/records/vendors/new?copyFrom=${encodeURIComponent(id)}`} className="nit-btn" style={{ marginLeft: 'auto', textDecoration: 'none' }}>Make Copy</Link>
        <button className="nit-btn" onClick={async () => {
          if (!window.confirm('Void (inactivate) this record? History is kept; it stops appearing in pickers.')) return;
          try {
            const { data } = await axios.post(`/api/native-records/vendors/${encodeURIComponent(id)}/void`);
            window.alert(`Inactivated (NetSuite: ${data.nsSync}).`);
            window.location.reload();
          } catch (err) { window.alert(err.response?.data?.error || err.message); }
        }}>Void</button>
      </div>

      <div className="nit-tabs">
        {tabs.map((t) => (
          <button key={t.key} className={`nit-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
            {t.count != null && <span className="nit-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="nit-grid">
          <div className="nit-card">
            <h3>Identity</h3>
            <div className="nit-facts">
              <span>Name</span><strong>{record.name || '—'}</strong>
              <span>Full name</span><strong>{record.full_name || '—'}</strong>
              <span>Email</span><strong>{record.email || '—'}</strong>
              <span>Phone</span><strong>{record.phone || '—'}</strong>
              <span>Created in NS</span><strong>{fmtDate(record.date_created)}</strong>
            </div>
          </div>

          <div className="nit-card">
            <h3>A/P standing</h3>
            <div className="nit-facts">
              <span>Balance</span><strong>{fmtMoney(record.balance)}</strong>
              <span>Prepayment balance</span><strong>{fmtMoney(record.prepayment_balance)}</strong>
              <span>Unbilled orders</span><strong>{fmtMoney(record.unbilled_orders)}</strong>
              <span>Terms</span><strong>{record.payment_terms || '—'}</strong>
            </div>
          </div>

          <div className="nit-card">
            <h3>Payment identity (1099)</h3>
            <div className="nit-facts">
              <span>1099 eligible</span><strong>{yesNo(record.is_1099_eligible)}</strong>
              <span>Tax ID (TIN)</span><strong>{record.tax_id_num || '—'}</strong>
              <span>Print on check as</span><strong>{record.print_on_check_as || '—'}</strong>
            </div>
            {record.tax_id_num == null && record.ns_raw == null && (
              <p className="nit-caption">
                TIN + check-printing name arrive with the capture backfill (migration 20260705) —
                until then the 1099 number exists only in NetSuite.
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'pos' && (
        <div className="nit-card">
          <h3>Recent purchase orders</h3>
          {recentPurchaseOrders.length === 0 && <div className="nit-empty">No synced POs for this vendor.</div>}
          {recentPurchaseOrders.length > 0 && (
            <div className="nit-table-wrap">
              <table className="nit-table">
                <thead><tr><th>PO</th><th>Date</th><th>Status</th><th className="r">Total</th><th>Memo</th></tr></thead>
                <tbody>
                  {recentPurchaseOrders.map((po) => (
                    <tr key={po.id}>
                      <td><Link to={`/records/purchase-orders/${encodeURIComponent(po.id)}`}><code>{po.order_number || po.ns_internal_id}</code></Link></td>
                      <td>{fmtDate(po.order_date)}</td>
                      <td className="nit-muted">{statusLabel(PO_STATUS, po.status)}</td>
                      <td className="r">{fmtMoney(po.total_amount)}</td>
                      <td className="nit-muted">{po.memo || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="nit-caption">Newest 20 from the synced mirror.</p>
        </div>
      )}

      {tab === 'bills' && (
        <div className="nit-card">
          <h3>Recent bills</h3>
          {recentBills.length === 0 && <div className="nit-empty">No synced bills for this vendor.</div>}
          {recentBills.length > 0 && (
            <div className="nit-table-wrap">
              <table className="nit-table">
                <thead><tr><th>Bill</th><th>Date</th><th>Due</th><th>Status</th><th className="r">Total</th><th className="r">Remaining</th></tr></thead>
                <tbody>
                  {recentBills.map((b) => (
                    <tr key={b.id}>
                      <td><code>{b.bill_number || b.id}</code></td>
                      <td>{fmtDate(b.bill_date)}</td>
                      <td>{fmtDate(b.due_date)}</td>
                      <td className="nit-muted">{b.status}</td>
                      <td className="r">{fmtMoney(b.total_amount)}</td>
                      <td className="r">{fmtMoney(b.amount_remaining)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="nit-caption">Newest 10 from the synced mirror. Full A/P lives in Count.</p>
        </div>
      )}

      {tab === 'fields' && (
        <AllFieldsPanel
          family="vendor"
          nsRaw={record.ns_raw}
          overrides={{
            taxidnum: record.tax_id_num,
            printoncheckas: record.print_on_check_as,
            terms: record.payment_terms,
          }}
        />
      )}

      {tab === 'attachments' && <AttachmentsTab recordType="vendor" recordId={id} />}
    </div>
  );
}
